import { Emitter } from "@core/events/emitter";
import {
  IDENTITY_STAGE_POSE,
  type ConsoleStagePose,
  type IConsolePlacementModel,
} from "./IConsolePlacementModel";

/**
 * `ConsolePlacementModel` — headless implementation of
 * {@link IConsolePlacementModel}. Read that interface first; it carries the
 * whole coordinate-space argument and the deviation list.
 *
 * Headless on purpose (no `three` import, per `Campfire_Web_Runtime`): this
 * half is the state machine and the pose, so it can be driven from a ViewModel
 * or a slate button with no renderer in the picture.
 * `runtime/xr/XRConsolePlacementController.ts` is the half that owns the aim,
 * the visuals, and writing `engine.root`.
 */
export class ConsolePlacementModel implements IConsolePlacementModel {
  readonly onPlacementModeChanged = new Emitter<boolean>();
  readonly onStagePoseChanged = new Emitter<ConsoleStagePose>();
  readonly onAwaitingConfirmationChanged = new Emitter<boolean>();

  private m_isPlacementMode = false;
  private m_planeHeight = 0;
  private m_pose: ConsoleStagePose = IDENTITY_STAGE_POSE;
  /** Unity `m_initialPos` / `m_initialRot`, captured in `BeginPlacement`. */
  private m_initialPose: ConsoleStagePose = IDENTITY_STAGE_POSE;
  /**
   * `planeHeight` when the mode opened, alongside {@link m_initialPose}. The
   * pair is what "Reset" puts back, and it is captured ONCE per placement
   * rather than per gesture — see {@link revertToAiming}.
   */
  private m_initialPlaneHeight = 0;
  private m_isAwaitingConfirmation = false;

  get isPlacementMode(): boolean {
    return this.m_isPlacementMode;
  }

  get planeHeight(): number {
    return this.m_planeHeight;
  }

  get pose(): ConsoleStagePose {
    return this.m_pose;
  }

  get isAwaitingConfirmation(): boolean {
    return this.m_isAwaitingConfirmation;
  }

  beginPlacement(): void {
    if (this.m_isPlacementMode) return;
    // A fresh mode starts in the aim phase — belt for a path that left the
    // adjust phase behind (nothing does today; every exit below clears it).
    this.leaveAdjustPhase();
    // Unity copies the live object's pose onto the GHOST here (`:133-138`) but
    // does NOT stamp `m_initialPos/Rot` — those are set once in `Awake` and
    // again in `InitialPlacement`. So upstream's Cancel restores the stage to
    // its FIRST seating, discarding every placement made since, not just the
    // one being cancelled.
    //
    // **Deliberate divergence**: the capture is taken here, so Cancel means
    // "put it back where it was when I started" — which is what Cancel means
    // everywhere else in the app, and the only reading a wearer could predict.
    // The literal port is also unreachable as written: `InitialPlacement` is
    // unported (see the interface header), so upstream's baseline would be a
    // bare identity, and Cancel would fling the stage to the world origin
    // rather than undoing anything.
    //
    // The web has one pose for both roles — the stage IS the preview, since
    // upstream's `UpdateObjects` makes the real `Space` track the ghost every
    // frame anyway — so the capture is just remembering it.
    this.m_initialPose = this.m_pose;
    this.m_initialPlaneHeight = this.m_planeHeight;
    this.m_isPlacementMode = true;
    this.onPlacementModeChanged.emit(true);
  }

  endPlacement(): void {
    if (!this.m_isPlacementMode) return;
    // "Confirm" IS this method — the stage is already where the drag left it,
    // so committing is only a matter of closing the episode and the mode.
    // Cleared BEFORE the mode emit: `WorkflowRunModel` resumes a parked run on
    // that event and the slate unmounts with it, so a stale pending flag would
    // outlive the only surface that could answer it.
    this.leaveAdjustPhase();
    this.m_isPlacementMode = false;
    this.onPlacementModeChanged.emit(false);
  }

  cancelPlacement(): void {
    if (!this.m_isPlacementMode) return;
    this.resetPlacement();
    this.endPlacement();
  }

  moveTo(x: number, z: number): void {
    this.setPose({ x, y: this.m_planeHeight, z, yawDegrees: this.m_pose.yawDegrees });
  }

  dragTo(x: number, y: number, z: number, yawDegrees: number): void {
    if (!this.m_isPlacementMode) return;
    // The floor, and the same one `adjustHeight` refuses to cross. Clamped
    // rather than dropped (which is what `adjustHeight` does) because this is a
    // continuous absolute position, not an accumulating step: dropping the
    // frame would freeze the stage the moment the hand dipped below the floor
    // and leave it stuck there for the rest of the drag, where a clamp lets it
    // keep tracking the hand in X and Z while sliding along the floor.
    const clampedY = Math.max(0, y);
    // The height the wearer dragged to becomes the height the aim preserves.
    this.m_planeHeight = clampedY;
    this.setPose({ x, y: clampedY, z, yawDegrees: wrapDegrees(yawDegrees) });
  }

  requestConfirmation(): void {
    if (!this.m_isPlacementMode) return;
    this.setAwaitingConfirmation(true);
  }

  revertToAiming(): void {
    if (!this.m_isPlacementMode) return;
    this.m_planeHeight = this.m_initialPlaneHeight;
    this.setPose(this.m_initialPose);
    this.setAwaitingConfirmation(false);
  }

  rotate(deltaDegrees: number): void {
    if (deltaDegrees === 0) return;
    this.setPose({ ...this.m_pose, yawDegrees: wrapDegrees(this.m_pose.yawDegrees + deltaDegrees) });
  }

  adjustHeight(deltaHeight: number): void {
    // Unity `Move(float)`: the console rises only while the ACCUMULATOR stays
    // at or above the floor, and the accumulator is what the next aim-driven
    // move forces the Y back to. A negative step that would go below zero is
    // dropped WHOLE rather than clamped, which is upstream's
    // `if (newHeight >= 0)` — a clamp would let a fast downward hold walk the
    // accumulator and the pose out of step.
    //
    // Upstream still calls `UpdateObjects()` on the rejected branch; returning
    // early here is equivalent, because that call re-writes a pose that did
    // not change. Not an oversight — the emitter would just fire for nothing.
    const next = this.m_planeHeight + deltaHeight;
    if (next < 0) return;
    this.m_planeHeight = next;
    this.setPose({ ...this.m_pose, y: this.m_pose.y + deltaHeight });
  }

  resetHeight(): void {
    this.m_planeHeight = 0;
  }

  resetPlacement(): void {
    // Unity `ResetPlacement` (`:272-280`) restores the captured pose VERBATIM
    // and separately zeroes `m_planeHeight`, accepting that the two can then
    // disagree — a stage captured at 20 cm comes back at 20 cm with the
    // accumulator at zero, and the next aim-driven move drops it to the floor.
    // Reproduced exactly; do not "reconcile" the pair here — and note this is
    // NOT what the slate's "Reset" calls, which is `revertToAiming` and does
    // keep the two in step. This one is Cancel's Unity-faithful leg.
    this.leaveAdjustPhase();
    this.m_planeHeight = 0;
    this.setPose(this.m_initialPose);
  }

  reset(): void {
    this.leaveAdjustPhase();
    this.m_planeHeight = 0;
    this.m_initialPlaneHeight = 0;
    this.m_initialPose = IDENTITY_STAGE_POSE;
    this.setPose(IDENTITY_STAGE_POSE);
    this.endPlacement();
  }

  private leaveAdjustPhase(): void {
    this.setAwaitingConfirmation(false);
  }

  private setAwaitingConfirmation(next: boolean): void {
    if (this.m_isAwaitingConfirmation === next) return;
    this.m_isAwaitingConfirmation = next;
    this.onAwaitingConfirmationChanged.emit(next);
  }

  private setPose(next: ConsoleStagePose): void {
    const current = this.m_pose;
    if (
      current.x === next.x &&
      current.y === next.y &&
      current.z === next.z &&
      current.yawDegrees === next.yawDegrees
    ) {
      return;
    }
    this.m_pose = next;
    this.onStagePoseChanged.emit(next);
  }
}

/** Keep the stored yaw in (-180, 180] so it stays readable in a debug print. */
function wrapDegrees(degrees: number): number {
  let d = degrees % 360;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}
